import { FormikProps } from 'formik';
import { Box, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useEffect } from 'react';
import LabeledInput from '../../Common/LabeledInput';
import { FormValues } from '../../../utils/validations/officeDetailsForm';
import { StyledCheckbox } from '../SetAvailability/SetAvailabilityStep';
import CustomPhoneNumberInput from '../../CustomPhoneNumberInput/CustomPhoneNumberInput';
import { useAppSelector } from '../../../redux/hooks';
import { fetchCountryCode } from '../../../redux/booking/bookingSlice';

function OfficeDetails({
  formik,
  hasPermissions,
}: {
  formik: FormikProps<FormValues>;
  hasPermissions: boolean;
}) {
  const { t } = useTranslation();
  const countryCode = useAppSelector((state) => state.booking.countryCode);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!countryCode) {
      dispatch(fetchCountryCode());
    }
  }, [countryCode]);

  const handlePhoneNumberChange = (pNumber: string) => {
    formik.setFieldValue('phoneNumber', pNumber);
  };

  return (
    <form onSubmit={formik.handleSubmit} noValidate>
      <Grid display="flex" flexDirection="column" gap={2} maxWidth={300}>
        <Grid display="flex" flexDirection="column">
          <LabeledInput
            label={t('bookings.inPersonConsultationsStep.address')}
            id="address"
            type="text"
            name="address"
            value={formik.values.address}
            handleChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.address ? formik.errors.address : undefined}
            placeholder={t('bookings.inPersonConsultationsStep.placeholder')}
            minRows={4}
            maxRows={4}
            multiline
            required
            maxLength={250}
            disabled={!hasPermissions}
          />
          <Grid display="flex" flexDirection="row" alignItems="center" ml={-1}>
            <StyledCheckbox
              onClick={() =>
                formik.setFieldValue('isAddressPublic', !formik.values.isAddressPublic)
              }
              checked={formik.values.isAddressPublic}
              inputProps={{ 'aria-label': 'controlled' }}
              checkedIcon={
                <Box
                  component="img"
                  src="/assets/icons/check-box-checked.svg"
                  width={20}
                  height={20}
                />
              }
              icon={
                <Box component="img" src="/assets/icons/check-box.svg" width={20} height={20} />
              }
              disabled={!hasPermissions}
            />
            <Typography variant="meta" color="primary">
              {t('bookings.inPersonConsultationsStep.makePublic')}
            </Typography>
          </Grid>
        </Grid>
        <Grid display="flex" flexDirection="column">
          <CustomPhoneNumberInput
            name="phoneNumber"
            value={formik.values.phoneNumber as string}
            handleChange={handlePhoneNumberChange}
            onBlur={formik.handleBlur}
            error={formik.touched.phoneNumber ? formik.errors.phoneNumber : undefined}
            countryCode={countryCode}
            disabled={!hasPermissions}
          />
          <Grid display="flex" flexDirection="row" alignItems="center" ml={-1}>
            <StyledCheckbox
              inputProps={{ 'aria-label': 'controlled' }}
              onClick={() =>
                formik.setFieldValue('isPhoneNumberPublic', !formik.values.isPhoneNumberPublic)
              }
              checked={formik.values.isPhoneNumberPublic}
              checkedIcon={
                <Box
                  component="img"
                  src="/assets/icons/check-box-checked.svg"
                  width={20}
                  height={20}
                />
              }
              icon={
                <Box component="img" src="/assets/icons/check-box.svg" width={20} height={20} />
              }
              disabled={!hasPermissions}
            />
            <Typography variant="meta" color="primary">
              {t('bookings.inPersonConsultationsStep.makePublic')}
            </Typography>
          </Grid>
        </Grid>
        <Typography variant="meta" color="primary">
          {t('bookings.inPersonConsultationsStep.footNote')}
        </Typography>
      </Grid>
    </form>
  );
}

export default OfficeDetails;
